import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Description as DocumentIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { usePartners } from '../../hooks/usePartners';
import s3Service from '../../services/s3Service';

const rowStyles = { cursor: 'pointer' };
const expandedRowBg = { backgroundColor: 'action.hover' };

const priorityCard = {
  borderColor: 'primary.main',
  borderWidth: 2,
  backgroundColor: 'background.paper',
  boxShadow: 1,
};
const statusChangeChip = { fontSize: '0.7rem', fontWeight: 'bold' };
const documentChip = { size: 'small', color: 'success', fontSize: '0.7rem' };
const detailChip = { fontWeight: 'bold', fontSize: '0.75rem', height: '24px', '& .MuiChip-label': { px: 1 } };

const fields = [
  { key: 'statusChange', label: 'jobHistory.fields.statusChange', icon: <HistoryIcon />, priority: true },
  { key: 'notes', label: 'jobHistory.fields.notes', icon: <DocumentIcon /> },
  { key: 'submissionDate', label: 'jobHistory.fields.submissionDate', icon: <CalendarIcon /> },
  { key: 'approvalDate', label: 'jobHistory.fields.approvalDate', icon: <CalendarIcon /> },
  { key: 'rejectionDate', label: 'jobHistory.fields.rejectionDate', icon: <CalendarIcon /> },
  { key: 'projectStartDate', label: 'jobHistory.fields.projectStartDate', icon: <CalendarIcon /> },
  { key: 'projectEndDate', label: 'jobHistory.fields.projectEndDate', icon: <CalendarIcon /> },
  { key: 'officialClosingDate', label: 'jobHistory.fields.officialClosingDate', icon: <CalendarIcon /> },
  { key: 'lastWorkDate', label: 'jobHistory.fields.lastWorkDate', icon: <CalendarIcon /> },
  { key: 'projectNumber', label: 'jobHistory.fields.projectNumber' },
  { key: 'projectDuration', label: 'jobHistory.fields.projectDuration' },
  { key: 'rejectionReason', label: 'jobHistory.fields.rejectionReason' },
  { key: 'commissionDocument', label: 'jobHistory.fields.commissionDocument', icon: <DocumentIcon /> },
  { key: 'firstPhaseDocument', label: 'jobHistory.fields.firstPhaseDocument', icon: <DocumentIcon /> },
  { key: 'noPartnerNeeded', label: 'jobHistory.fields.noPartnerNeeded' },
  { key: 'partnerFirmId', label: 'jobHistory.fields.partnerFirmId' },
];

const EXTENSION_TYPE_LABELS = {
  UZATMA: 'Proje Uzatıldı',
  OTELME: 'Proje Ötelendi',
  DIGER: 'Proje Tarihi Değişti',
};

const EXTENSION_TYPE_COLORS = {
  UZATMA: 'primary',
  OTELME: 'warning',
  DIGER: 'default',
};

const JobHistorySection = ({ jobDetails, projectExtensions, styles }) => {
  const { t } = useTranslation();
  const { partners } = usePartners();
  const [expandedRows, setExpandedRows] = useState({});

  const mergedItems = useMemo(() => {
    const statusItems = (jobDetails || []).map(item => ({
      ...item,
      _kind: 'status',
      _sortDate: item.created_at,
    }));

    const extensionItems = (projectExtensions || []).map((ext, index) => ({
      id: `ext-${index}`,
      _kind: 'extension',
      _sortDate: ext.createdAt,
      ext,
    }));

    return [...statusItems, ...extensionItems].sort(
      (a, b) => new Date(b._sortDate) - new Date(a._sortDate)
    );
  }, [jobDetails, projectExtensions]);

  if (!mergedItems.length) return null;

  const handleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getFileNameFromKey = (key) => {
    if (!key) return '';
    const parts = key.split('/');
    return parts[parts.length - 1] || key;
  };

  const handleViewDocument = async (documentKey) => {
    try {
      const signedUrl = await s3Service.getSignedUrl(documentKey);
      if (signedUrl) window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Belge görüntüleme hatası:', error);
    }
  };

  const renderDetailData = (detailData) => {
    if (!detailData) return null;

    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {fields
          .sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0))
          .map((field) => {
            const value = detailData[field.key];
            if (value === null || value === undefined || value === '') return null;
            if (field.key === 'noPartnerNeeded' && (value === null || value === undefined)) return null;

            return (
              <Grid item xs={12} sm={6} md={4} key={field.key}>
                <Card variant="outlined" sx={{ p: 1, ...(field.priority && priorityCard) }}>
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      {field.icon && (
                        <Box sx={{ mr: 1, ...(field.priority && { color: 'primary.main' }) }}>
                          {field.icon}
                        </Box>
                      )}
                      <Typography variant="caption" color="textSecondary">
                        {t(field.label)}
                      </Typography>
                    </Box>
                    <Box component="div">
                      {field.key === 'statusChange' ? (
                        <Chip onClick={()=> {}} label={value} size="small" color="primary" variant="filled" sx={{ ...detailChip, ...statusChangeChip }} />
                      ) : field.key === 'noPartnerNeeded' ? (
                        value ? t('common.yes') : t('common.no')
                      ) : field.key === 'partnerFirmId' ? (
                        (() => {
                          const partner = partners?.find(p => p.id === value);
                          return partner ? partner.partner_name : value;
                        })()
                      ) : field.key.includes('Date') ? (
                        formatDate(value)
                      ) : field.key.includes('Document') ? (
                        <Chip
                          onClick={() => value && handleViewDocument(value)}
                          label={getFileNameFromKey(value)}
                          icon={<DocumentIcon />}
                          sx={{ ...documentChip, cursor: value ? 'pointer' : 'default' }}
                        />
                      ) : (
                        value
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
      </Grid>
    );
  };

  const renderExtensionDetail = (ext) => (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {ext.startDate && (
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ mr: 1 }}><CalendarIcon fontSize="small" /></Box>
                <Typography variant="caption" color="textSecondary">Yeni Başlangıç Tarihi</Typography>
              </Box>
              <Typography variant="body2">{formatDate(ext.startDate)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
      {ext.endDate && (
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ mr: 1 }}><CalendarIcon fontSize="small" /></Box>
                <Typography variant="caption" color="textSecondary">Yeni Bitiş Tarihi</Typography>
              </Box>
              <Typography variant="body2">{formatDate(ext.endDate)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
      {ext.reason && (
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ p: 1 }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>Sebep</Typography>
              <Typography variant="body2">{ext.reason}</Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  return (
    <>
      <Divider sx={styles?.divider} />
      <Box>
        <Typography variant="h6" color="text.primary" sx={{ ...styles?.sectionTitle, mb: 1.5 }}>
          {t('jobStatusUpdate.history.title')} ({mergedItems.length})
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="40px" />
                <TableCell>{t('jobHistory.table.date')}</TableCell>
                <TableCell>{t('jobHistory.table.user')}</TableCell>
                <TableCell>{t('jobHistory.table.statusChange')}</TableCell>
                <TableCell>{t('jobHistory.table.notes')}</TableCell>
                <TableCell>{t('jobHistory.table.type')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mergedItems.map((item) => {
                const isExpanded = expandedRows[item.id];

                if (item._kind === 'extension') {
                  const ext = item.ext;
                  const typeLabel = EXTENSION_TYPE_LABELS[ext.type] || ext.type;
                  const typeColor = EXTENSION_TYPE_COLORS[ext.type] || 'default';
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow
                        hover
                        sx={{ ...(isExpanded ? expandedRowBg : {}), ...rowStyles }}
                        onClick={() => handleRowExpand(item.id)}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{formatDateTime(ext.createdAt)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2">{ext.createdByName || '-'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip
                          onClick={()=> {}}
                            label={typeLabel}
                            size="small"
                            color={typeColor}
                            variant="filled"
                            icon={<EventIcon />}
                            sx={statusChangeChip}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {ext.reason || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip onClick={()=> {}} label="Proje Tarihi" size="small" color={typeColor} variant="outlined" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2 }}>
                              <Typography variant="h6" gutterBottom>{typeLabel}</Typography>
                              {renderExtensionDetail(ext)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={item.id}>
                    <TableRow
                      hover
                      sx={{ ...(isExpanded ? expandedRowBg : {}), ...rowStyles }}
                      onClick={() => handleRowExpand(item.id)}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        <IconButton size="small">
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2">{formatDateTime(item.created_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2">
                          {item.user ? `${item.user.first_name} ${item.user.last_name}` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {item.detail_data?.statusChange ? (
                          <Chip
                          onClick={()=> {}}
                            label={item.detail_data.statusChange}
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={statusChangeChip}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" color="textSecondary">
                          {item.detail_data?.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip onClick={()=> {}} label={item.job_detail_type} size="small" color="primary" variant="outlined" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                              {t('jobHistory.details')}
                            </Typography>
                            {renderDetailData(item.detail_data)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
};

export default JobHistorySection;
